import { CheckCircle2 } from 'lucide-react';
import { useT } from '../../i18n';
import { formatNumber } from '../../lib/reportFormat';
import type { PipelineReport, ProductType } from '../../types/api';
import { stageLabelKey } from '../../lib/stageLabels';
import styles from './AttentionPanel.module.css';

export interface AttentionPanelProps {
  report: PipelineReport;
  /** Selected Dashboard tab (#75) — stage labels follow the route wording. */
  productType?: ProductType | null;
}

interface AttentionItem {
  key: string;
  tone: 'warn' | 'bad' | 'ok';
  text: string;
}

/**
 * Auto-hints for the selected period, derived from the live report:
 * deals missing a confidence rating (stages 3–4), deals sitting on pause,
 * and stages where more deals dropped than advanced.
 */
export function AttentionPanel({ report, productType }: AttentionPanelProps) {
  const { t, locale } = useT();

  const items: AttentionItem[] = [];

  // Confidence missing on stages where it is mandatory (PRIORITIZATION and later).
  const noConfidence = report.stages
    .filter(
      (s) =>
        s.stage === 'PRIORITIZATION' ||
        s.stage === 'REALIZATION' ||
        s.stage === 'INTEGRATOR_SELECTION' ||
        s.stage === 'TKP',
    )
    .reduce((n, s) => n + s.byConfidence.none, 0);
  if (noConfidence > 0) {
    items.push({
      key: 'no-confidence',
      tone: 'warn',
      text: t('pipeline.attentionNoConfidenceCount', {
        count: formatNumber(noConfidence, locale),
      }),
    });
  }

  // Deals currently on pause (snapshot).
  const paused = report.stages.reduce((n, s) => n + s.currentlyPaused, 0);
  if (paused > 0) {
    items.push({
      key: 'paused',
      tone: 'warn',
      text: t('pipeline.attentionPausedCount', { count: formatNumber(paused, locale) }),
    });
  }

  // Stages leaking more than they pass on. REALIZATION is skipped: before
  // #28 its "advanced" was always 0 by design (WIN is a separate event type).
  // TKP is skipped for the same reason — it is the final stage now.
  for (const s of report.stages) {
    if (s.stage === 'REALIZATION' || s.stage === 'TKP') continue;
    if (s.dropped > 0 && s.dropped > s.advanced) {
      items.push({
        key: `drop-${s.stage}`,
        tone: 'bad',
        text: t('pipeline.attentionDropSpike', {
          stage: t(stageLabelKey(s.stage, productType)),
          dropped: formatNumber(s.dropped, locale),
          advanced: formatNumber(s.advanced, locale),
        }),
      });
    }
  }

  if (items.length === 0) {
    items.push({ key: 'ok', tone: 'ok', text: t('pipeline.attentionAllGood') });
  }

  return (
    <div className={styles.card}>
      {items.map((item) => (
        <div className={styles.item} key={item.key}>
          {item.tone === 'ok' ? (
            <CheckCircle2 size={14} className={styles.okIcon} aria-hidden />
          ) : (
            <span
              className={`${styles.dot} ${item.tone === 'bad' ? styles.dotBad : styles.dotWarn}`}
              aria-hidden
            />
          )}
          <div>{item.text}</div>
        </div>
      ))}
    </div>
  );
}
